import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Button,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  IconButton,
  Typography,
  Divider,
  CircularProgress,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  GetApp as DownloadIcon,
  ViewColumn as ViewColumnIcon,
  FilterList as FilterListIcon,
  KeyboardArrowDown as ArrowDownIcon,
  FolderOpen as FolderIcon,
  List as ListIcon,
  HelpOutline as HelpOutlineIcon,
} from '@mui/icons-material';
import Joyride, { ACTIONS, EVENTS, STATUS } from 'react-joyride';
import TourOutline from 'components/shared/TourOutline';
import { useEntityTour } from 'hooks/useTour';
import { DOCHUB_TOUR, DEFAULT_TOUR_STYLES, TOUR_TITLES, CustomTooltip } from 'config/tours';
// import BoxShadow from 'components/shared/BoxShadow'; // Using Box directly for embedded mode
import DocumentViewerModal from 'components/shared/DocumentViewerModal';
import EmailConfirmationModal from 'components/VendorDetails/EmailConfirmationModal';
import Snackbar from 'components/shared/Snackbar';

import DocHubFilter from './DocHubFilter';
import DocHubTable from './DocHubTable';
import DocHubPagination from './DocHubPagination';
import FileCabinet from './FileCabinet';
import AddToFolderModal from './FileCabinet/AddToFolderModal';
import DragDropProvider from './FileCabinet/DragDropProvider';
import { organizationState } from 'redux/organizationSlice';
import { certificatesState, clientActions } from 'redux/clientSlice';
import { vendorState, vendorActions } from 'redux/vendorSlice';
import { loggedInUserState } from 'redux/userSlice';
import { getProjectLabel } from 'constants/organization_types';
import certificate_actions from 'constants/certificate_actions';
import { exportToCSV } from 'utils/exportUtils';
import { getCertificateFiles } from 'services/appResources';
import fileCabinetService from 'services/fileCabinet';
import { useNavPath, getNavigationPath } from 'constants/routes';
import { generateClient } from 'aws-amplify/api';
import * as queries from 'graphql/queries';
import './styles/DocHub.scss';

const client = generateClient();

const DocHub = ({ vendorFilterId = null, hideVendorColumn = false, embedded = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  
  const { selectedClient, userOrgType } = useSelector(organizationState);
  const { certificates } = useSelector(certificatesState);
  const { vendors } = useSelector(vendorState);
  const { loggedInUser } = useSelector(loggedInUserState);
  const projects = useSelector(state => state.client.projects?.[selectedClient?.clientID]) || [];
  const NAV_PATH = useNavPath(userOrgType);
  
  // State management
  const [documents, setDocuments] = useState([]);
  const [documentRequests, setDocumentRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [filterVisible, setFilterVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [columnAnchorEl, setColumnAnchorEl] = useState(null);
  const [selectedColumns, setSelectedColumns] = useState(
    hideVendorColumn 
      ? ['id', 'name', 'type', 'status', 'uploaded', 'lastModified', 'expirationDate', 'folderPath', 'actions']
      : ['id', 'name', 'type', 'status', 'subcontractor', 'uploaded', 'lastModified', 'expirationDate', 'folderPath', 'actions']
  );
  const [activeTab, setActiveTab] = useState(0); // 0 = List, 1 = File Cabinet
  
  // Sorting state
  const [sortBy, setSortBy] = useState('uploaded');
  const [sortAsc, setSortAsc] = useState(false);
  
  // Document viewer state
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [loadingDocument, setLoadingDocument] = useState(false);
  
  // Email modal state
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailCertificateId, setEmailCertificateId] = useState(null);
  const [message, setMessage] = useState(null);
  
  // Add to Folder modal state
  const [addToFolderModalOpen, setAddToFolderModalOpen] = useState(false);
  const [selectedDocumentForFolder, setSelectedDocumentForFolder] = useState(null);
  const [folders, setFolders] = useState([]);
  
  // Folder paths state
  const [documentFolderPaths, setDocumentFolderPaths] = useState({});
  
  // State for folder navigation target (when navigating from folder path click)
  const [folderNavigationTarget, setFolderNavigationTarget] = useState(null);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalResults: 0,
    resultsPerPage: 25
  });
  
  // Filter state
  const [filters, setFilters] = useState({
    dateRange: 'all',
    dateFrom: '',
    dateTo: '',
    status: [],
    subcontractor: '',
    documentType: [],
    expirationRange: 'all',
    expirationFrom: '',
    expirationTo: '',
  });

  // Tour state
  const [tourKey, setTourKey] = useState(0);
  const {
    isRunning: isTourRunning,
    stepIndex: tourStepIndex,
    completedSteps,
    startTour,
    stopTour,
    goToStep,
  } = useEntityTour(DOCHUB_TOUR.id, selectedClient?.clientID);

  // Available columns for column selector
  const availableColumns = [
    { id: 'id', label: 'ID', required: true },
    { id: 'name', label: 'Document Name', required: true },
    { id: 'type', label: 'Type' },
    { id: 'category', label: 'Category' },
    { id: 'status', label: 'Status' },
    ...(hideVendorColumn ? [] : [{ id: 'subcontractor', label: 'Subcontractor' }]),
    { id: 'uploaded', label: 'Uploaded' },
    { id: 'lastModified', label: 'Last Modified' },
    { id: 'expirationDate', label: 'Expiration Date' },
    { id: 'folderPath', label: 'Folder Path' },
    { id: 'actions', label: 'Actions', required: true },
  ];
  
  // Clear data and reset state when client changes
  useEffect(() => {
    // Clear existing data when client changes
    setDocuments([]);
    setSearchTerm('');
    setFilters({
      dateRange: 'all',
      dateFrom: '',
      dateTo: '',
      status: [],
      subcontractor: '',
      documentType: [],
      expirationRange: 'all',
      expirationFrom: '',
      expirationTo: '',
    });
    setPagination({
      currentPage: 1,
      totalPages: 1,
      totalResults: 0,
      resultsPerPage: 25
    });
    // Show loading state immediately when switching clients
    setLoading(true);
  }, [selectedClient?.clientID]);

  // Load certificates and vendors on mount or client change
  useEffect(() => {
    const loadInitialData = async () => {
      if (selectedClient?.clientID) {
        setInitialLoading(true);
        console.log('DocHub: Loading initial data for client:', selectedClient.clientID);
        
        try {
          // Always fetch fresh data for the new client
          console.log('DocHub: Fetching vendors for client:', selectedClient.clientID);
          await dispatch(vendorActions.fetchVendors(selectedClient.clientID));
          
          console.log('DocHub: Fetching certificates for client:', selectedClient.clientID);
          await dispatch(clientActions.getAllClientCertificates(selectedClient.clientID));
        } finally {
          setInitialLoading(false);
        }
      }
    };
    
    loadInitialData();
  }, [selectedClient?.clientID]);
  
  // Load document requests when client changes
  useEffect(() => {
    if (selectedClient?.clientID) {
      loadDocumentRequests();
    }
  }, [selectedClient?.clientID]);

  // Transform and filter documents when certificates or filters change
  useEffect(() => {
    if (selectedClient?.clientID) {
      loadDocuments();
    }
  }, [certificates, documentRequests, filters, pagination.currentPage, pagination.resultsPerPage, searchTerm, sortBy, sortAsc, selectedClient?.clientID]);
  
  // Load document requests from API
  const loadDocumentRequests = async () => {
    try {
      // First, load document requests
      const result = await client.graphql({
        query: queries.documentRequestByClient,
        variables: {
          clientID: selectedClient.clientID,
          filter: { status: { eq: "UPLOADED" } },
          limit: 1000
        }
      });
      
      const requests = result.data?.documentRequestByClient?.items || [];
      console.log('DocHub: Loaded document requests:', requests.length);
      
      // Then load uploads for each request
      const requestsWithUploads = await Promise.all(
        requests.map(async (request) => {
          try {
            const uploadsResult = await client.graphql({
              query: queries.documentUploadByRequest,
              variables: {
                documentRequestID: request.id,
                limit: 100
              }
            });
            
            return {
              ...request,
              uploads: {
                items: uploadsResult.data?.documentUploadByRequest?.items || []
              }
            };
          } catch (error) {
            console.error(`Error loading uploads for request ${request.id}:`, error);
            return {
              ...request,
              uploads: { items: [] }
            };
          }
        })
      );
      
      setDocumentRequests(requestsWithUploads);
    } catch (error) {
      console.error('Error loading document requests:', error);
    }
  };

  // Transform certificates and documents into unified format
  const loadDocuments = async () => {
    setLoading(true);
    try {
      console.log('DocHub: Loading documents, certificates count:', certificates?.length || 0);
      console.log('DocHub: Document requests count:', documentRequests?.length || 0);
      
      // Transform certificates into document format
      const transformedCertDocs = transformCertificatesToDocuments(certificates || []);
      
      // Transform document requests into document format
      const transformedRequestDocs = transformDocumentRequestsToDocuments(documentRequests || []);
      
      // Link uploaded documents to certificates as related documents
      const certificateMap = new Map(transformedCertDocs.filter(doc => doc.type === 'certificate').map(doc => [doc.id, doc]));
      
      transformedRequestDocs.forEach(uploadDoc => {
        if (uploadDoc.associatedCertificateIds && uploadDoc.associatedCertificateIds.length > 0) {
          // Add this document to each associated certificate's related documents
          uploadDoc.associatedCertificateIds.forEach(certId => {
            const cert = certificateMap.get(certId);
            if (cert) {
              // Add upload to certificate's related documents
              if (!cert.relatedDocuments.some(rd => rd.id === uploadDoc.id)) {
                cert.relatedDocuments.push({
                  id: uploadDoc.id,
                  name: uploadDoc.name
                });
              }
              
              // Add certificate reference to upload's related documents
              if (!uploadDoc.relatedDocuments.some(rd => rd.id === cert.id)) {
                uploadDoc.relatedDocuments.push({
                  id: cert.id,
                  name: cert.name
                });
              }
            }
          });
        }
      });
      
      // Combine both sources
      const transformedDocs = [...transformedCertDocs, ...transformedRequestDocs];
      console.log('DocHub: Total transformed documents count:', transformedDocs.length);
      
      // Apply filters and search
      const filteredDocs = applyFiltersAndSearch(transformedDocs);
      console.log('DocHub: Filtered documents count:', filteredDocs.length);
      
      // Apply sorting
      const sortedDocs = applySorting(filteredDocs);
      
      // Apply pagination
      const startIndex = (pagination.currentPage - 1) * pagination.resultsPerPage;
      const endIndex = startIndex + pagination.resultsPerPage;
      const paginatedDocs = sortedDocs.slice(startIndex, endIndex);
      
      setDocuments(paginatedDocs);
      setPagination(prev => ({
        ...prev,
        totalResults: filteredDocs.length,
        totalPages: Math.ceil(filteredDocs.length / prev.resultsPerPage)
      }));
      
      // Load folder paths for the paginated documents
      if (paginatedDocs.length > 0 && selectedClient?.clientID) {
        loadDocumentFolderPaths(paginatedDocs);
      }
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Load folder paths for documents
  const loadDocumentFolderPaths = async (documents) => {
    try {
      // Extract certificate IDs from documents
      const certificateIDs = documents.map(doc => doc.id).filter(id => id);
      
      if (certificateIDs.length === 0) {
        return;
      }
      
      console.log('Loading folder paths for', certificateIDs.length, 'documents');
      
      // Get folder paths for all documents
      const folderPathsData = await fileCabinetService.getDocumentsFolderPaths(
        certificateIDs, 
        selectedClient.clientID
      );
      
      console.log('Loaded folder paths:', folderPathsData);
      setDocumentFolderPaths(folderPathsData);
    } catch (error) {
      console.error('Error loading document folder paths:', error);
    }
  };
  
  // Transform certificates to document format
  const transformCertificatesToDocuments = (certificates) => {
    const docs = [];
    
    if (!Array.isArray(certificates)) {
      console.warn('DocHub: certificates is not an array:', certificates);
      return docs;
    }
    
    certificates.forEach(cert => {
      // Get the primary certificate filename
      let certificateName = '';
      
      // Debug log to see what data we have
      console.log('Certificate data:', {
        id: cert.id,
        acord25File: cert.acord25File,
        files: cert.files,
        insuredName: cert.insuredName
      });
      
      // Check if we have an acord25File (primary certificate file)
      if (cert.acord25File) {
        // Extract just the filename from the path if it contains slashes
        certificateName = cert.acord25File.split('/').pop();
      } 
      // Otherwise check if we have files array with at least one file
      else if (cert.files && cert.files.length > 0) {
        // Use the first file as the primary document
        const firstFile = cert.files[0];
        certificateName = typeof firstFile === 'string' ? firstFile.split('/').pop() : firstFile.name || firstFile;
      }
      
      // If still no filename, create a fallback name
      if (!certificateName) {
        // Try to use insured name if available
        let fallbackName = '';
        if (cert.insuredName && cert.insuredName.trim()) {
          fallbackName = cert.insuredName.trim();
        } else if (cert.insured && cert.insured.trim()) {
          fallbackName = cert.insured.trim();
        }
        
        // Add certificate date if available and not default
        if (cert.certificateDate && cert.certificateDate !== '0000-01-01') {
          const dateStr = new Date(cert.certificateDate).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          });
          fallbackName = fallbackName ? `${fallbackName} - ${dateStr}` : `Certificate - ${dateStr}`;
        }
        
        // If still no name, use certificate type or shortened ID
        if (!fallbackName) {
          if (cert.certificateType) {
            fallbackName = `${cert.certificateType.replace('_', ' ')} Certificate`;
          } else {
            // Use last 8 characters of ID as fallback
            const shortId = cert.id.split('-').pop().substring(0, 8);
            fallbackName = `Certificate-${shortId}`;
          }
        }
        
        certificateName = fallbackName;
      }
      
      // Find the vendor for this certificate
      const vendorForCert = vendors?.find(v => v.id === cert.clientVendorID);
      
      const mainDoc = {
        id: cert.id,
        name: certificateName,
        type: 'certificate',
        category: 'Insurance',
        status: cert.status,
        subcontractor: vendorForCert?.vendor?.name || '-',
        subcontractorId: cert.clientVendorID,
        vendorId: vendorForCert?.vendorID, // The actual vendor ID
        clientVendorId: cert.clientVendorID, // The junction table ID for navigation
        uploaded: cert.createdAt,
        lastModified: cert.updatedAt,
        expirationDate: cert.firstExp,
        relatedDocuments: [],
        certificateData: cert,
      };
      
      // Add supplemental documents if they exist
      if (cert.supplementalCoiFiles) {
        try {
          const supplementalDocs = typeof cert.supplementalCoiFiles === 'string' 
            ? JSON.parse(cert.supplementalCoiFiles) 
            : cert.supplementalCoiFiles;
          
          if (Array.isArray(supplementalDocs)) {
            supplementalDocs.forEach((doc, index) => {
              mainDoc.relatedDocuments.push({
                id: doc.id || `${cert.id}-supp-${index}`,
                name: doc.filename
              });
              
              // Add supplemental doc as separate document
              docs.push({
                id: doc.id || `${cert.id}-supp-${index}`,
                name: doc.filename,
                type: 'policy',
                category: doc.policyTypes?.join(', ') || 'Other',
                status: 'Approved', // Default status for supplemental docs
                subcontractor: mainDoc.subcontractor,
                subcontractorId: cert.clientVendorID,
                vendorId: vendorForCert?.vendorID, // The actual vendor ID
                clientVendorId: cert.clientVendorID, // The junction table ID for navigation
                uploaded: doc.uploadedDate,
                lastModified: doc.uploadedDate,
                expirationDate: null,
                relatedDocuments: [{
                  id: cert.id,
                  name: mainDoc.name
                }],
                supplementalData: doc,
                parentCertificateId: cert.id,
              });
            });
          }
        } catch (e) {
          console.error('Error parsing supplemental documents:', e);
        }
      }
      
      // Add files array documents (skip the first file if it's the same as acord25File)
      if (cert.files && Array.isArray(cert.files)) {
        cert.files.forEach((file, index) => {
          // Skip if this file is the acord25File (already included as main doc)
          if (cert.acord25File && file === cert.acord25File) {
            return;
          }
          
          const fileId = `${cert.id}-file-${index}`;
          // Extract just the filename from the path
          const fileName = typeof file === 'string' ? file.split('/').pop() : (file.name || file);
          
          mainDoc.relatedDocuments.push({
            id: fileId,
            name: fileName
          });
          
          docs.push({
            id: fileId,
            name: fileName,
            type: 'document',
            category: 'Other',
            status: 'N/A',
            subcontractor: mainDoc.subcontractor,
            subcontractorId: cert.clientVendorID,
            vendorId: vendorForCert?.vendorID, // The actual vendor ID
            clientVendorId: cert.clientVendorID, // The junction table ID for navigation
            uploaded: cert.createdAt,
            lastModified: cert.updatedAt,
            expirationDate: null,
            relatedDocuments: [{
              id: cert.id,
              name: mainDoc.name
            }],
            fileData: file,
            parentCertificateId: cert.id,
          });
        });
      }
      
      // Only add the document if it's not a MULTI AGENT certificate
      // Check both the name and the certificate type
      const isMultiAgent = mainDoc.name === "MULTI AGENT Certificate" || 
                          cert.certificateType === "MULTI_AGENT";
      
      if (!isMultiAgent) {
        docs.push(mainDoc);
      }
    });
    
    return docs;
  };

  // Transform document requests to document format
  const transformDocumentRequestsToDocuments = (requests) => {
    const docs = [];
    
    if (!Array.isArray(requests)) {
      console.warn('DocHub: document requests is not an array:', requests);
      return docs;
    }
    
    requests.forEach(request => {
      if (!request.uploads || request.uploads.items.length === 0) {
        return; // Skip requests without uploads
      }
      
      // Get vendor info
      const vendor = vendors.find(v => v.id === request.clientVendorID);
      const vendorName = vendor?.vendorName || vendor?.name || request.vendorName || 'Unknown Vendor';
      
      // Parse associated certificate IDs if present
      let associatedCertificateIds = [];
      if (request.associatedCertificateIds) {
        associatedCertificateIds = request.associatedCertificateIds;
      }
      
      // Create a document entry for each upload
      request.uploads.items.forEach(upload => {
        const categoryLabels = {
          'POLICY': 'Policy',
          'ENDORSEMENT': 'Endorsement',
          'CONTRACT': 'Contract',
          'LEASE': 'Lease',
          'DRIVERS_LICENSE': "Driver's License",
          'OTHER': 'Other Document'
        };
        
        // Check if this upload should be linked to certificates
        const isRelatedToCertificate = associatedCertificateIds.length > 0 && 
          ['POLICY', 'ENDORSEMENT', 'OTHER'].includes(upload.category);
        
        docs.push({
          id: `doc-upload-${upload.id}`,
          name: upload.fileName,
          type: 'document_upload',
          documentType: categoryLabels[upload.category] || upload.category,
          status: 'UPLOADED',
          subcontractor: vendorName,
          vendorId: request.clientVendorID,
          projectId: request.projectID,
          uploaded: upload.uploadedAt,
          lastModified: upload.updatedAt,
          expirationDate: null,
          s3Key: upload.s3Key,
          s3Bucket: upload.s3Bucket,
          fileSize: upload.fileSize,
          fileType: upload.fileType,
          requestId: request.id,
          requestedBy: request.requestedBy,
          requestedAt: request.requestedAt,
          category: upload.category,
          annotations: upload.annotation,
          certificateData: null,
          parentCertificateId: isRelatedToCertificate ? associatedCertificateIds[0] : null,
          associatedCertificateIds: associatedCertificateIds,
          isDocumentUpload: true,
          relatedDocuments: [] // Will be populated when certificates are loaded
        });
      });
    });
    
    return docs;
  };
  
  // Apply filters and search
  const applyFiltersAndSearch = (docs) => {
    let filtered = [...docs];
    
    // Exclude REQUESTED status and null status records by default
    filtered = filtered.filter(doc => doc.status !== 'REQUESTED' && doc.status != null);
    
    // Apply vendor filter if provided (for embedded use in vendor details)
    if (vendorFilterId) {
      filtered = filtered.filter(doc => 
        doc.subcontractorId === vendorFilterId || 
        doc.clientVendorId === vendorFilterId ||
        doc.vendorId === vendorFilterId
      );
    }
    
    // Apply search
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(doc => 
        doc.name.toLowerCase().includes(search) ||
        doc.subcontractor.toLowerCase().includes(search) ||
        doc.id.toLowerCase().includes(search)
      );
    }
    
    // Apply status filter
    if (filters.status.length > 0) {
      filtered = filtered.filter(doc => filters.status.includes(doc.status));
    }
    
    // Apply document type filter
    if (filters.documentType.length > 0) {
      filtered = filtered.filter(doc => filters.documentType.includes(doc.type));
    }
    
    // Apply subcontractor filter
    if (filters.subcontractor) {
      filtered = filtered.filter(doc => 
        doc.subcontractor.toLowerCase().includes(filters.subcontractor.toLowerCase())
      );
    }
    
    // Apply date filters
    // TODO: Implement date range filtering
    
    return filtered;
  };
  
  // Apply sorting to documents
  const applySorting = (docs) => {
    const sorted = [...docs];
    
    sorted.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];
      
      // Handle null/undefined values
      if (!aValue && aValue !== 0) return sortAsc ? 1 : -1;
      if (!bValue && bValue !== 0) return sortAsc ? -1 : 1;
      
      // Handle different data types
      if (sortBy === 'name' || sortBy === 'subcontractor' || sortBy === 'type' || sortBy === 'category') {
        // String comparison
        aValue = aValue.toString().toLowerCase();
        bValue = bValue.toString().toLowerCase();
      } else if (sortBy === 'uploaded' || sortBy === 'lastModified' || sortBy === 'expirationDate') {
        // Date comparison
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      } else if (sortBy === 'relatedDocuments') {
        // Array length comparison
        aValue = aValue?.length || 0;
        bValue = bValue?.length || 0;
      }
      
      // Perform comparison
      if (aValue < bValue) return sortAsc ? -1 : 1;
      if (aValue > bValue) return sortAsc ? 1 : -1;
      return 0;
    });
    
    return sorted;
  };
  
  // Handle export to CSV
  const handleExportCSV = () => {
    const exportData = documents.map(doc => ({
      ID: doc.id,
      'Document Name': doc.name,
      Type: doc.type,
      Category: doc.category || '-',
      Status: doc.status,
      Subcontractor: doc.subcontractor,
      'Uploaded Date': new Date(doc.uploaded).toLocaleDateString(),
      'Last Modified': new Date(doc.lastModified).toLocaleDateString(),
      'Expiration Date': doc.expirationDate ? new Date(doc.expirationDate).toLocaleDateString() : '-',
      'Related Documents': doc.relatedDocuments.map(r => r.id).join(', '),
    }));
    
    exportToCSV(exportData, 'documents-export');
  };
  
  // Handle column selection
  const handleColumnToggle = (columnId) => {
    const column = availableColumns.find(c => c.id === columnId);
    if (column?.required) return; // Don't allow toggling required columns
    
    setSelectedColumns(prev => 
      prev.includes(columnId)
        ? prev.filter(id => id !== columnId)
        : [...prev, columnId]
    );
  };
  
  // Handle page change
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  };
  
  // Handle rows per page change
  const handleRowsPerPageChange = (newRowsPerPage) => {
    setPagination(prev => ({ 
      ...prev, 
      resultsPerPage: newRowsPerPage,
      currentPage: 1 
    }));
  };
  
  // Handle document click - navigate to certificate or open PDF viewer
  const handleDocumentClick = async (docId) => {
    // First check in current page documents
    let doc = documents.find(d => d.id === docId);
    
    // If not found in current page, search in all transformed documents
    if (!doc) {
      const allDocs = transformCertificatesToDocuments(certificates || []);
      doc = allDocs.find(d => d.id === docId);
    }
    
    if (!doc) {
      console.error('Document not found:', docId);
      return;
    }
    
    if (doc.type === 'certificate' && doc.certificateData) {
      // Navigate to certificate details page
      const path = getNavigationPath(NAV_PATH.CLIENT_CERTIFICATE_DETAILS, {
        clientId: selectedClient.organizationID,
        vendorId: doc.certificateData.clientVendorID,
        certificateId: doc.id
      });
      navigate(path, { state: { referrer: location.pathname } });
    } else {
      // Open PDF viewer for other documents
      setLoadingDocument(true);
      try {
        // Get the document URL based on type
        let files = [];
        if (doc.supplementalData && doc.parentCertificateId) {
          // This is a supplemental document
          files = [{
            name: doc.supplementalData.filename,
            isSupplementalCoiFile: true,
            fullPath: `SupplementalCoverageDocumentation/${doc.supplementalData.insuranceType}/${doc.supplementalData.documentType}/${doc.supplementalData.filename}`
          }];
        } else if (doc.fileData && doc.parentCertificateId) {
          // This is a regular file from the files array
          files = [doc.fileData];
        } else if (doc.isDocumentUpload && doc.s3Key) {
          // This is a document upload - fetch from S3
          const { getUrl } = await import('@aws-amplify/storage');
          const { url } = await getUrl({
            path: doc.s3Key,
          });
          
          setSelectedDocument({
            name: doc.name,
            filename: doc.name,
            fileUrl: url.toString(),
            fileType: doc.fileType || 'application/pdf'
          });
          setViewerOpen(true);
          return;
        } else if (doc.type === 'document' && !doc.parentCertificateId) {
          // This might be a standalone document
          console.warn('Standalone document without parent certificate:', doc);
          return;
        }
        
        if (files.length > 0 && doc.parentCertificateId) {
          const certificateId = doc.parentCertificateId;
          const result = await getCertificateFiles(certificateId, files);
          
          if (result && result.length > 0) {
            setSelectedDocument({
              name: doc.name,
              filename: doc.name,
              fileUrl: result[0].content,
              fileType: doc.fileType || 'application/pdf'
            });
            setViewerOpen(true);
          }
        }
      } catch (error) {
        console.error('Error loading document:', error);
      } finally {
        setLoadingDocument(false);
      }
    }
  };
  
  // Handle vendor/subcontractor click
  const handleVendorClick = (vendorId, clientVendorId) => {
    if (!clientVendorId) return;
    
    console.log('Navigating to vendor - vendorId:', vendorId, 'clientVendorId:', clientVendorId);
    
    // Navigate to vendor details using clientVendorId (junction table ID) for consistency with certificate URLs
    const path = getNavigationPath(NAV_PATH.CLIENT_VENDOR_DETAILS, {
      clientId: selectedClient.organizationID,
      vendorId: clientVendorId, // Use the clientVendorId for navigation
      referrer: 'VENDORS',
      referrerId: selectedClient.organizationID // Use client ID as referrer ID when coming from DocHub
    });
    
    console.log('Generated path:', path);
    
    navigate(path, { state: { referrer: location.pathname } });
  };
  
  // Handle document download
  const handleDownloadDocument = async (doc) => {
    if (!doc) return;
    
    try {
      let fileUrl = null;
      
      if (doc.type === 'certificate' && doc.certificateData) {
        // Get certificate files
        const cert = doc.certificateData;
        const filesToDownload = cert.acord25File ? [cert.acord25File] : cert.files || [];
        if (filesToDownload.length > 0) {
          const files = await getCertificateFiles(cert.id, filesToDownload);
          if (files && files.length > 0) {
            fileUrl = files[0].content;
          }
        }
      } else if (doc.supplementalData && doc.parentCertificateId) {
        // Get supplemental document file with proper path
        const supplementalFile = {
          name: doc.supplementalData.filename,
          isSupplementalCoiFile: true,
          fullPath: `SupplementalCoverageDocumentation/${doc.supplementalData.insuranceType}/${doc.supplementalData.documentType}/${doc.supplementalData.filename}`
        };
        const files = await getCertificateFiles(doc.parentCertificateId, [supplementalFile]);
        if (files && files.length > 0) {
          fileUrl = files[0].content;
        }
      } else if (doc.fileData && doc.parentCertificateId) {
        // Handle regular files from the files array
        const files = await getCertificateFiles(doc.parentCertificateId, [doc.fileData]);
        if (files && files.length > 0) {
          fileUrl = files[0].content;
        }
      } else if (doc.type === 'document' && doc.parentCertificateId) {
        // Handle generic document files - these are from the files array
        const fileName = doc.name;
        if (fileName) {
          const files = await getCertificateFiles(doc.parentCertificateId, [fileName]);
          if (files && files.length > 0) {
            fileUrl = files[0].content;
          }
        }
      }
      
      if (fileUrl) {
        // Open in new tab
        window.open(fileUrl, '_blank', 'noopener,noreferrer');
      } else {
        setMessage({
          text: 'No file available for download',
          severity: 'warning'
        });
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      setMessage({
        text: 'Unable to download document',
        severity: 'error'
      });
    }
  };
  
  // Handle share certificate
  const handleShareDocument = (doc) => {
    if (!doc || doc.type !== 'certificate') return;
    
    setEmailCertificateId(doc.id);
    setEmailModalOpen(true);
  };
  
  // Handle delete document
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  const handleDeleteDocument = (doc) => {
    setDocumentToDelete(doc);
    setDeleteConfirmOpen(true);
  };
  
  const confirmDeleteDocument = async () => {
    if (!documentToDelete || deleteLoading) return;
    
    setDeleteLoading(true);
    console.log('Deleting document:', documentToDelete);
    
    try {
      let deleteSuccess = false;
      
      // Determine the type of document and call appropriate delete mutation
      if (documentToDelete.type === 'certificate') {
        // Delete certificate
        const { deleteCERTIFICATE } = await import('../../graphql/mutations');
        const result = await client.graphql({
          query: deleteCERTIFICATE,
          variables: { input: { id: documentToDelete.id } }
        });
        console.log('Certificate delete result:', result);
        deleteSuccess = true;
        
        setMessage({
          severity: 'success',
          text: 'Certificate deleted successfully'
        });
      } else if (documentToDelete.isDocumentUpload) {
        // Extract the actual upload ID from the document ID
        // The ID format is "doc-upload-{actualId}"
        const uploadId = documentToDelete.id.replace('doc-upload-', '');
        console.log('Deleting document upload with ID:', uploadId);
        
        try {
          const { deleteDOCUMENTUPLOAD } = await import('../../graphql/mutations');
          const result = await client.graphql({
            query: deleteDOCUMENTUPLOAD,
            variables: { input: { id: uploadId } }
          });
          console.log('Document upload delete result:', result);
          
          if (result?.data?.deleteDOCUMENTUPLOAD) {
            deleteSuccess = true;
            setMessage({
              severity: 'success',
              text: 'Document deleted successfully'
            });
          } else {
            throw new Error('Delete operation returned no data');
          }
        } catch (deleteError) {
          console.error('Error deleting document upload:', deleteError);
          // Check if it's a "not found" error
          if (deleteError?.errors?.[0]?.message?.includes('No item found')) {
            // Document doesn't exist, treat as successful deletion
            deleteSuccess = true;
            setMessage({
              severity: 'warning',
              text: 'Document was already deleted or not found'
            });
          } else {
            throw deleteError;
          }
        }
      } else if (documentToDelete.supplementalData) {
        // Handle supplemental document deletion
        // This would require updating the certificate's supplementalCoiFiles
        setMessage({
          severity: 'info',
          text: 'Supplemental document deletion not yet implemented'
        });
      } else if (documentToDelete.parentCertificateId && documentToDelete.fileData) {
        // This is a file attached to a certificate
        setMessage({
          severity: 'info',
          text: 'Certificate file deletion not yet implemented'
        });
      } else {
        console.warn('Unknown document type:', documentToDelete);
        setMessage({
          severity: 'warning',
          text: 'Unable to determine document type for deletion'
        });
      }
      
      // Close dialog first
      setDeleteConfirmOpen(false);
      setDocumentToDelete(null);
      
      // Only refresh if deletion was successful
      if (deleteSuccess) {
        // Optimistically remove the document from the current display
        setDocuments(prevDocs => prevDocs.filter(doc => doc.id !== documentToDelete.id));
        
        // Then refresh the data from backend
        if (documentToDelete.type === 'certificate') {
          // Reload certificates from the backend
          dispatch(clientActions.getAllClientCertificates(selectedClient.clientID));
        }
        
        // Reload document requests for document uploads
        if (documentToDelete.isDocumentUpload) {
          await loadDocumentRequests();
        }
        
        // Refresh the display after a delay
        setTimeout(() => {
          loadDocuments();
        }, 500);
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      setMessage({
        severity: 'error',
        text: 'Failed to delete document. Please try again.'
      });
      setDeleteConfirmOpen(false);
      setDocumentToDelete(null);
    } finally {
      setDeleteLoading(false);
    }
  };
  
  // Handle email sent result
  const onEmailSentResult = (success) => {
    if (success) {
      setMessage({
        text: "Successfully sent email notification.",
        severity: "success",
      });
    } else {
      setMessage({
        text: "Unable to send email notification.",
        severity: "error",
      });
    }
  };
  
  // Generate upload link for sharing certificates
  const getUploadLink = async (certificateId, _projectID, mode) => {
    const certificate = certificates.find(cert => cert.id === certificateId);
    if (!certificate) {
      console.error('Certificate not found:', certificateId);
      return null;
    }
    
    const vendor = vendors.find(v => v.id === certificate.clientVendorID);
    if (!vendor) {
      console.error('Vendor not found for certificate:', certificate.clientVendorID);
      return null;
    }
    
    const vendorId = vendor.vendorID || vendor.vendor?.id;
    if (!vendorId) {
      console.error('Vendor ID not found in vendor object:', vendor);
      return null;
    }
    
    return window.location.origin + `/vendor/upload/${selectedClient.id}/${vendorId}/${certificateId}${mode ? `/${mode}` : ""}`;
  };
  
  // Find the selected certificate and vendor for email modal
  const selectedCertificate = useMemo(() => {
    if (!emailCertificateId || !certificates) return null;
    return certificates.find(cert => cert.id === emailCertificateId);
  }, [emailCertificateId, certificates]);

  const selectedVendor = useMemo(() => {
    if (!selectedCertificate || !vendors) return null;
    return vendors.find(v => v.id === selectedCertificate.clientVendorID);
  }, [selectedCertificate, vendors]);
  
  const handleTabChange = (_event, newValue) => {
    setActiveTab(newValue);
    // Load folders when switching to File Cabinet tab
    if (newValue === 1 && folders.length === 0) {
      // Only load folders if we have a selected client
      if (selectedClient?.clientID) {
        loadFolders();
      }
    }
  };

  // Handle folder path click to navigate to File Cabinet tab and specific folder
  const handleFolderPathClick = (folderID, folderName) => {
    console.log('Navigating to folder:', folderID, folderName);
    
    // Set the navigation target
    setFolderNavigationTarget({ folderID, folderName });
    
    // Switch to File Cabinet tab
    setActiveTab(1);
    
    // Load folders if not already loaded
    if (folders.length === 0 && selectedClient?.clientID) {
      loadFolders();
    }
  };

  // Load folders for Add to Folder modal
  const loadFolders = async () => {
    try {
      const folderList = await fileCabinetService.getFolders(selectedClient.clientID);
      setFolders(folderList);
    } catch (error) {
      console.error('Error loading folders:', error);
      // Don't show error for expected table not found errors
      if (error.errors && error.errors[0]?.message) {
        const errorMessage = error.errors[0].message;
        if (errorMessage.includes('foldersByClient') || 
            errorMessage.includes('Validation error') ||
            errorMessage.includes('Cannot return null')) {
          // Silently fail - tables not deployed yet
          setFolders([]);
        }
      }
    }
  };

  // Handle Add to Folder action
  const handleAddToFolder = (document) => {
    setSelectedDocumentForFolder(document);
    setAddToFolderModalOpen(true);
    // Load folders if not already loaded
    if (folders.length === 0) {
      loadFolders();
    }
  };

  // Handle actual addition to folder
  const handleAddDocumentToFolder = async (folderID) => {
    try {
      const result = await fileCabinetService.addToFolder(
        folderID,
        selectedDocumentForFolder,
        loggedInUser.id,
        selectedClient.clientID,
        selectedClient.organizationID
      );
      
      // Show message if document is already in other folders
      if (result.alreadyInFolders && result.alreadyInFolders > 0) {
        setMessage({
          severity: 'info',
          text: `Document added to folder. Note: This document is also in ${result.alreadyInFolders} other folder(s).`,
        });
      } else {
        setMessage({
          severity: 'success',
          text: 'Document added to folder successfully',
        });
      }
      
      setAddToFolderModalOpen(false);
      setSelectedDocumentForFolder(null);
    } catch (error) {
      console.error('Error adding document to folder:', error);
      setMessage({
        severity: 'error',
        text: 'Failed to add document to folder',
      });
    }
  };

  // Tour callback - handles tab switching between List and File Cabinet
  const handleTourCallback = useCallback((data) => {
    const { action, index, status, type } = data;

    // Handle tour completion
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      stopTour();
      return;
    }

    // Handle close action
    if (action === ACTIONS.CLOSE) {
      stopTour();
      return;
    }

    // Calculate next step based on action
    if (type === EVENTS.STEP_AFTER) {
      const isGoingBack = action === ACTIONS.PREV;
      const nextStepIndex = isGoingBack ? index - 1 : index + 1;

      // Bounds check
      if (nextStepIndex < 0 || nextStepIndex >= DOCHUB_TOUR.steps.length) {
        if (nextStepIndex >= DOCHUB_TOUR.steps.length) {
          stopTour();
        }
        return;
      }

      const nextStep = DOCHUB_TOUR.steps[nextStepIndex];

      // Handle tab switching based on next step's data
      if (nextStep?.data?.tab !== undefined) {
        const targetTab = nextStep.data.tab;
        if (targetTab !== activeTab) {
          setActiveTab(targetTab);
          setTimeout(() => {
            goToStep(nextStepIndex);
          }, 200);
          return;
        }
      }

      // Normal step navigation
      goToStep(nextStepIndex);
    }
  }, [activeTab, goToStep, stopTour]);

  // Start tour handler
  const handleStartTour = useCallback(() => {
    // Start on List tab
    setActiveTab(0);
    setTourKey(prev => prev + 1);
    setTimeout(() => {
      startTour();
    }, 100);
  }, [startTour]);

  // Handle clicking on steps in the tour outline
  const handleStepClick = useCallback((index) => {
    const step = DOCHUB_TOUR.steps[index];
    if (step?.data?.tab !== undefined) {
      const targetTab = step.data.tab;
      if (targetTab !== activeTab) {
        setActiveTab(targetTab);
        setTimeout(() => {
          goToStep(index);
        }, 200);
        return;
      }
    }
    requestAnimationFrame(() => {
      setTimeout(() => {
        goToStep(index);
      }, 50);
    });
  }, [activeTab, goToStep]);

  return (
    <DragDropProvider>
      <Box sx={{ p: embedded ? 0 : 2, position: 'relative', minHeight: embedded ? 'auto' : '600px', width: '100%', boxShadow: embedded ? 0 : 3, borderRadius: embedded ? 0 : 2 }}>
        <Box className="dochub-container">
        {/* Tab Navigation - only show when not embedded */}
        {!embedded && (
          <Box className="dochub-tabs" sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={activeTab} onChange={handleTabChange} aria-label="DocHub tabs">
              <Tab
                className="dochub-list-tab"
                icon={<ListIcon />}
                iconPosition="start"
                label="List"
                sx={{ minHeight: 48 }}
              />
              <Tab
                className="dochub-file-cabinet-tab"
                icon={<FolderIcon />}
                iconPosition="start"
                label="File Cabinet"
                sx={{ minHeight: 48 }}
              />
            </Tabs>
          </Box>
        )}

        {/* Tab Content */}
        {activeTab === 0 ? (
          <>
            {/* List View - Original DocHub Content */}
            {/* Controls Bar */}
            <Box className="dochub-controls">
          <Box className="dochub-controls-left">
            <Button
              className="dochub-filters-btn"
              startIcon={<FilterListIcon />}
              onClick={() => setFilterVisible(!filterVisible)}
              variant={filterVisible ? "contained" : "outlined"}
              size="small"
            >
              Filters
            </Button>
            
            <TextField
              size="small"
              placeholder={embedded ? "Filter by document name or ID" : "Filter by document name, subcontractor, or ID"}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              className="dochub-search"
            />
          </Box>
          
          <Box className="dochub-controls-right">
            <Button
              className="dochub-export-btn"
              startIcon={<DownloadIcon />}
              onClick={handleExportCSV}
              variant="outlined"
              size="small"
            >
              Export CSV
            </Button>

            <Button
              className="dochub-column-selector"
              endIcon={<ArrowDownIcon />}
              onClick={(e) => setColumnAnchorEl(e.currentTarget)}
              variant="outlined"
              size="small"
            >
              Default Column View
            </Button>

            {!embedded && (
              <Tooltip title="Take a tour of DocHub">
                <IconButton
                  onClick={handleStartTour}
                  size="small"
                  sx={{ ml: 1 }}
                >
                  <HelpOutlineIcon />
                </IconButton>
              </Tooltip>
            )}

            <Menu
              anchorEl={columnAnchorEl}
              open={Boolean(columnAnchorEl)}
              onClose={() => setColumnAnchorEl(null)}
            >
              {availableColumns.map(column => (
                <MenuItem 
                  key={column.id}
                  onClick={() => handleColumnToggle(column.id)}
                  disabled={column.required}
                >
                  <input
                    type="checkbox"
                    checked={selectedColumns.includes(column.id)}
                    readOnly
                    style={{ marginRight: '8px' }}
                  />
                  {column.label}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Box>
        
        <Divider />
        
        {/* Filter Panel */}
        {filterVisible && (
          <>
            <DocHubFilter
              filters={filters}
              onFiltersChange={setFilters}
              onClose={() => setFilterVisible(false)}
            />
            <Divider />
          </>
        )}
        
        {/* Documents Table */}
        <Box className="dochub-table">
          <DocHubTable
            documents={documents}
            loading={loading || initialLoading}
            selectedColumns={selectedColumns}
            sortBy={sortBy}
            sortAsc={sortAsc}
            setSortBy={setSortBy}
            setSortAsc={setSortAsc}
            onNavigateToDocument={handleDocumentClick}
            onNavigateToVendor={handleVendorClick}
            onShareDocument={handleShareDocument}
            onDownloadDocument={handleDownloadDocument}
            onAddToFolder={handleAddToFolder}
            onDeleteDocument={handleDeleteDocument}
            documentFolderPaths={documentFolderPaths}
            onFolderPathClick={handleFolderPathClick}
          />
        </Box>
        
        {/* Pagination */}
        {!loading && !initialLoading && documents.length > 0 && (
          <DocHubPagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            totalResults={pagination.totalResults}
            resultsPerPage={pagination.resultsPerPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        )}
        {/* Initial Loading Overlay */}
        {initialLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              zIndex: 10,
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={48} />
              <Typography variant="body1" sx={{ mt: 2 }}>
                Loading documents...
              </Typography>
            </Box>
          </Box>
        )}
        
            {/* Document Loading Overlay */}
            {loadingDocument && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  zIndex: 10,
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <CircularProgress size={48} />
                  <Typography variant="body1" sx={{ mt: 2 }}>
                    Loading document...
                  </Typography>
                </Box>
              </Box>
            )}
          </>
        ) : (
          /* File Cabinet View */
          <FileCabinet 
            documents={documents} 
            navigationTarget={folderNavigationTarget}
            onNavigationComplete={() => setFolderNavigationTarget(null)}
          />
        )}
      </Box>
      
      {/* Document Viewer Modal */}
      {selectedDocument && (
        <DocumentViewerModal
          open={viewerOpen}
          onClose={() => {
            setViewerOpen(false);
            setSelectedDocument(null);
          }}
          document={selectedDocument}
          onDownload={() => {
            // Handle download
            const link = document.createElement('a');
            link.href = selectedDocument.fileUrl;
            link.download = selectedDocument.name;
            link.click();
          }}
        />
      )}
      
      {/* Email Confirmation Modal */}
      {selectedVendor && (
        <EmailConfirmationModal
          open={emailModalOpen}
          onClose={() => {
            setEmailModalOpen(false);
            setEmailCertificateId(null);
          }}
          clientName={selectedClient?.name}
          clientId={selectedClient?.clientID}
          clientLogo={selectedClient?.logo}
          vendorName={selectedVendor?.vendor?.name}
          clientVendorID={selectedVendor?.id}
          vendorEmail={selectedVendor?.vendor?.email || ''}
          templates={[certificate_actions.SHARE_CERTIFICATE]}
          clientTemplates={selectedClient?.templates}
          onEmailSentResult={onEmailSentResult}
          certificateId={emailCertificateId}
          getUploadLink={getUploadLink}
          vendorProjects={selectedClient?.mode === 'PROJECT' ? projects : []}
          defaultProject={projects?.find((project) => project.isDefault)}
          loggedInUser={loggedInUser}
          useClientDefaultProject={selectedClient?.useDefaultProject}
          additionalRecipients={[
            ...(selectedClient?.usePrimaryNotificationEmailAddress ? [selectedClient?.primaryNotificationEmailAddress] : []), 
            ...(Array.isArray(selectedVendor?.vendor?.additionalEmails) ? selectedVendor.vendor.additionalEmails : [])
          ]}
          projectLabel={getProjectLabel(selectedClient)}
        />
      )}
      
      {/* Add to Folder Modal */}
      <AddToFolderModal
        open={addToFolderModalOpen}
        onClose={() => {
          setAddToFolderModalOpen(false);
          setSelectedDocumentForFolder(null);
        }}
        folders={folders}
        onSubmit={handleAddDocumentToFolder}
        documentName={selectedDocumentForFolder?.name}
      />
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setDocumentToDelete(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{documentToDelete?.name}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setDeleteConfirmOpen(false);
              setDocumentToDelete(null);
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmDeleteDocument} 
            color="error" 
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? <CircularProgress size={20} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for messages */}
      <Snackbar
        message={message}
        setMessage={setMessage}
      />

      {/* Joyride Tour */}
      {!embedded && (
        <Joyride
          key={`dochub-tour-${tourKey}`}
          steps={DOCHUB_TOUR.steps}
          stepIndex={tourStepIndex}
          run={isTourRunning}
          continuous
          showSkipButton
          showProgress
          disableScrolling={false}
          disableScrollParentFix
          spotlightClicks
          callback={handleTourCallback}
          tooltipComponent={CustomTooltip}
          styles={DEFAULT_TOUR_STYLES}
        />
      )}

      {/* Tour Outline Sidebar */}
      {!embedded && isTourRunning && (
        <TourOutline
          steps={DOCHUB_TOUR.steps}
          currentStep={tourStepIndex}
          completedSteps={completedSteps}
          onStepClick={handleStepClick}
          title={TOUR_TITLES[DOCHUB_TOUR.id]}
        />
      )}
    </Box>
    </DragDropProvider>
  );
};

export default DocHub;